import React, { useEffect, useState } from "react";

import { Pagination as MuiPagination, TablePaginationProps, TextField } from "@mui/material";
import {
  GridPagination,
  gridFilteredTopLevelRowCountSelector,
  gridPageSizeSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from "@mui/x-data-grid";

const getPageCount = (rowCount: number, pageSize: number): number => {
  if (pageSize > 0 && rowCount > 0) {
    return Math.ceil(rowCount / pageSize);
  }
  return 1;
};

const Pagination: React.FC<Pick<TablePaginationProps, "page" | "onPageChange" | "className">> = ({
  page,
  onPageChange,
  className,
}) => {
  const apiRef = useGridApiContext();
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const visibleTopLevelRowCount = useGridSelector(apiRef, gridFilteredTopLevelRowCountSelector);
  const rootProps = useGridRootProps();
  const pageCount = getPageCount(rootProps.rowCount ?? visibleTopLevelRowCount, pageSize);
  const [inputValue, setInputValue] = useState<string>(String(page + 1));

  const handlePaginationChange = (event: any, newPage: number) => {
    setInputValue(String(newPage));
    onPageChange(event as any, newPage - 1);
  };

  useEffect(() => {
    setInputValue(String(page + 1));
  }, [page]);

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter") {
      const newPage = Number(inputValue);
      if (newPage > 0 && newPage <= pageCount) {
        handlePaginationChange(event, newPage);
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  return (
    <>
      <MuiPagination
        data-testid={"pagination"}
        color="primary"
        className={className}
        count={pageCount}
        page={page + 1}
        onChange={(event, newPage) => {
          handlePaginationChange(event, newPage);
        }}
      />
      <TextField
        data-testid={"input-page-number"}
        type="tel"
        value={inputValue}
        onKeyDown={handleKeyDown}
        onChange={handleInputChange}
        variant="outlined"
        size="small"
        style={{ flex: 1, minWidth: "75px" }}
        inputProps={{ min: 1, max: pageCount }}
        error={
          inputValue !== "" &&
          (isNaN(Number(inputValue)) || Number(inputValue) < 1 || Number(inputValue) > pageCount)
        }
      />
    </>
  );
};

const CustomGridPagination: React.FC<{ children: React.ReactNode }> = ({ ...props }) => {
  return <GridPagination ActionsComponent={Pagination} {...props} />;
};

export { CustomGridPagination };
